import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  MDBContainer,
  MDBCol,
  MDBRow,
  MDBBtn,
  MDBInput,
  MDBCheckbox,
} from 'mdb-react-ui-kit';
import CompanyNameModal from '../Modals/CompanyNameModal';
import SubscriptionPlansModal from '../Modals/SubscriptionPlansModal';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useMessageBox } from '../Common/MessageBox';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { Country, City } from "country-state-city";



const API_BASE_URL = `${config.API_BASE_URL}/api/auth`;

function Register({ onSwitchToLogin, onRegisterSuccess }) {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company_name: '',
    password: '',
    confirmPassword: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    phone_number: ''
  });
  const countries = Country.getAllCountries();
  const cities = City.getCitiesOfCountry(formData.country);




  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { showError, showSuccess, showWarning, MessageBoxComponent } = useMessageBox();

  // Company Name Modal States
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const [showPlansModal, setShowPlansModal] = useState(false);
  const [pendingPlanContext, setPendingPlanContext] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  // =====================
  // NORMAL REGISTRATION
  // =====================
  const handleRegister = async (e) => {
    e.preventDefault();

   const {
      firstName,
      lastName,
      email,
      company_name,
      password,
      confirmPassword,
      country,
      city,
      address,
      postal_code,
      phone_number

    } = formData;


    if (!firstName || !email || !company_name || !password || !confirmPassword || !country || !city) {
      showWarning("Please fill all required fields");
      return;
    }



    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }


    if (phone_number && !isValidPhoneNumber(phone_number)) {
      showWarning("Invalid phone number");
      return;
    }


    const payload = {
        name: `${firstName} ${lastName}`.trim(),
        email,
        password,
        company_name,
        country,
        city,
        address,
        postal_code,
        phone_number
      };


    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        showError(data.message || "Registration failed");
        return;
      }

      showSuccess("Account created! Choose your subscription plan below.");
      setPendingPlanContext({
        mode: "email_signup",
        email,
        name: `${firstName} ${lastName}`.trim(),
        token: null,
      });
      setShowPlansModal(true);

    } catch (err) {
      console.error("API Error:", err);
      showError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // GOOGLE SIGNUP - STEP 1: Get User Info
  // =====================
  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);

        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        const userInfo = await userInfoResponse.json();

        // Always show company modal for Google signup
        setPendingGoogleUser({
          token: tokenResponse.access_token,
          userInfo: userInfo
        });
        setShowCompanyModal(true);

      } catch (err) {
        console.error('Google signup error:', err);
        showError('Google signup failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
      showError('Google login failed. Please try again.');
    },
  });

  // =====================
  // GOOGLE SIGNUP - STEP 2: Complete Auth with Company Name
  // =====================
  const completeGoogleAuth = async (token, userInfo, formData) => {
    try {
      const backendResponse = await fetch(`${API_BASE_URL}/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: userInfo.email,
          name: userInfo.name,
          ...formData, 
        }),
      });

      const backendData = await backendResponse.json();

      if (!backendResponse.ok) {
        showError(backendData.message || 'Google authentication failed');
        return { ok: false };
      }

      login(backendData.token, backendData.user);

      showSuccess(`Welcome ${backendData.user.name}! Choose your subscription plan.`);
      return {
        ok: true,
        token: backendData.token,
        user: backendData.user,
      };

    } catch (err) {
      console.error('Google auth completion error:', err);
      showError('Authentication failed. Please try again.');
      return { ok: false };
    }
  };

  // =====================
  // HANDLE COMPANY NAME SUBMISSION
  // =====================
  const handleCompanyNameSubmit = async (data) => {
    if (!pendingGoogleUser) return;

    const result = await completeGoogleAuth(
      pendingGoogleUser.token,
      pendingGoogleUser.userInfo,
      data
    );

    if (!result?.ok) return;

    setShowCompanyModal(false);
    setPendingGoogleUser(null);
    setPendingPlanContext({
      mode: "google_signup",
      email: result.user.email,
      name: result.user.name,
      token: result.token,
    });
    setShowPlansModal(true);
  };

  const handlePlansChooseFree = () => {
    const mode = pendingPlanContext?.mode;
    setShowPlansModal(false);
    setPendingPlanContext(null);
    if (mode === "google_signup") {
      onRegisterSuccess && onRegisterSuccess();
    } else {
      onSwitchToLogin();
    }
  };

  return (
    <>
      <MDBContainer fluid className="p-3" style={{ minHeight: '100vh' }}>
        <MDBRow className="justify-content-center align-items-center" style={{ minHeight: '100vh' }}>

          {/* LEFT: IMAGE SECTION */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="6"
            className="d-none d-md-flex align-items-center justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ maxWidth: '100%', width: '100%' }}>
              <img
                src="/images/man_in_brown_coat_shopify_ebay_ad.png"
                className="img-fluid"
                alt="Register"
                style={{ 
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxHeight: '80vh'
                }}
              />
            </div>
          </MDBCol>

          {/* RIGHT: REGISTER FORM */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="5" 
            xl="4"
            className="d-flex justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ width: '100%', maxWidth: '450px' }}>

              {/* LOGO & TITLE */}
              <div className="text-center mb-4">
                <img
                  src="/images/thriftlogo.png"
                  alt="ThriftLogo"
                  style={{ 
                    height: 'clamp(60px, 10vw, 80px)',
                    marginBottom: '1rem',
                    width: 'auto'
                  }}
                />
                <p className="text-muted" style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
                  Join us and start selling everywhere
                </p>
              </div>

              {/* GOOGLE SIGNUP BUTTON */}
              <MDBBtn
                className="mb-3 w-100 d-flex align-items-center justify-content-center"
                size="lg"
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  border: '1px solid #ddd',
                  padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}
                onClick={() => googleSignup()}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  "Signing up..."
                ) : (
                  <>
                    <img
                      src="/images/google-icon.svg"
                      alt="Google"
                      style={{ 
                        width: 'clamp(18px, 3vw, 22px)', 
                        height: 'clamp(18px, 3vw, 22px)', 
                        marginRight: '10px' 
                      }}
                    />
                    Sign up with Google
                  </>
                )}
              </MDBBtn>

              {/* DIVIDER */}
              <div className="divider d-flex align-items-center my-3">
                <p className="fw-bold mx-3 mb-0" style={{ color: '#888', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                  OR
                </p>
              </div>

              {/* REGISTER FORM */}
              <form onSubmit={handleRegister}>

                {/* NAME FIELDS */}
                <MDBRow className="mb-3">
                  <MDBCol size="6">
                    <MDBInput
                      label="First Name"
                      id="firstName"
                      type="text"
                      size="lg"
                      value={formData.firstName}
                      onChange={handleChange}
                      style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                    />
                  </MDBCol>
                  <MDBCol size="6">
                    <MDBInput
                      label="Last Name"
                      id="lastName"
                      type="text"
                      size="lg"
                      value={formData.lastName}
                      onChange={handleChange}
                      style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                    />
                  </MDBCol>
                </MDBRow>

                <MDBInput
                  label="Email address"
                  id="email"
                  type="email"
                  size="lg"
                  className="mb-3"
                  value={formData.email}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Company Name"
                  id="company_name"
                  type="text"
                  size="lg"
                  className="mb-3"
                  value={formData.company_name}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />
                {/* COUNTRY */}
                <select
                  id="country"
                  className="form-control mb-3"
                  value={formData.country}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      country: e.target.value,
                      city: "" // reset city when country changes
                    });
                  }}
                >
                  <option value="">Select Country</option>
                  {countries.map((c) => (
                    <option key={c.isoCode} value={c.isoCode}>
                      {c.name}
                    </option>
                  ))}
                </select>


                {/* CITY */}
                <select
                  id="city"
                  className="form-control mb-3"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={!formData.country}
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>


                <MDBInput
                  label="Business Address"
                  id="address"
                  type="text"
                  size="lg"
                  className="mb-3"
                  value={formData.address}
                  onChange={handleChange}
                />

                <MDBInput
                  label="Postal Code"
                  id="postal_code"
                  type="text"
                  size="lg"
                  className="mb-3"
                  value={formData.postal_code}
                  onChange={handleChange}
                />

               <label className="mb-1 fw-bold">Phone Number</label>

                  <PhoneInput
                    country={'pk'}
                    value={formData.phone_number}
                    countryCodeEditable={false}  
                    onChange={(phone, country) => {
                      setFormData({
                        ...formData,
                        phone_number: `+${phone}`,
                        country: country.name
                      });
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '48px',
                      fontSize: '16px',
                    }}
                    containerClass="mb-3"
                  />



                <MDBInput
                  label="Password"
                  id="password"
                  type="password"
                  size="lg"
                  className="mb-3"
                  value={formData.password}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Confirm Password"
                  id="confirmPassword"
                  type="password"
                  size="lg"
                  className="mb-3"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBCheckbox
                  name="terms"
                  id="terms"
                  label="I agree to the Terms and Conditions"
                  required
                  className="mb-3"
                  style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}
                />

                <MDBBtn
                  type="submit"
                  className="w-100"
                  size="lg"
                  disabled={loading}
                  style={{ 
                    backgroundColor: '#667eea',
                    padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </MDBBtn>

              </form>

              {/* LOGIN LINK */}
              <div className="text-center mt-3" style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>
                <p className="mb-0">
                  Already have an account?{' '}
                  <a 
                    href="#!" 
                    onClick={onSwitchToLogin} 
                    style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: '500' }}
                  >
                    Login here
                  </a>
                </p>
              </div>

            </div>
          </MDBCol>

        </MDBRow>
      </MDBContainer>

      {/* ============================================ */}
      {/* COMPANY NAME MODAL */}
      {/* ============================================ */}
      <CompanyNameModal
        show={showCompanyModal}
        userName={pendingGoogleUser?.userInfo?.name || ''}
        onSubmit={handleCompanyNameSubmit}
      />

      <SubscriptionPlansModal
        show={showPlansModal}
        context={pendingPlanContext?.mode}
        userEmail={pendingPlanContext?.email || ""}
        userName={pendingPlanContext?.name || ""}
        signupFeature={pendingPlanContext?.features || ""}
        authToken={pendingPlanContext?.token || null}
        checkoutSessionUrl={config.STRIPE_CHECKOUT_SESSION_URL}
        onChooseFree={handlePlansChooseFree}
      />

      <MessageBoxComponent />
    </>
  );
}

export default Register;